import React from 'react';
import classNames from 'classnames';
import css from './ListingPage.module.css';

import worksWithHomeyBadge from '../../assets/works-with-homey.png';
import bluetoothIcon from '../../assets/connectivity-icons/bluetooth.svg';
import cellularIcon from '../../assets/connectivity-icons/cellular.svg';
import ethernetIcon from '../../assets/connectivity-icons/ethernet.svg';
import infraredIcon from '../../assets/connectivity-icons/infrared.svg';
import matterIcon from '../../assets/connectivity-icons/matter.svg';
import poeIcon from '../../assets/connectivity-icons/poe.svg';
import radioIcon from '../../assets/connectivity-icons/radio.svg';
import threadIcon from '../../assets/connectivity-icons/thread.svg';
import wifiIcon from '../../assets/connectivity-icons/wi-fi.svg';
import zigbeeIcon from '../../assets/connectivity-icons/zigbee.svg';
import zWaveIcon from '../../assets/connectivity-icons/z-wave.svg';

const CONNECTIVITY_ICON_MAP = {
  bluetooth: bluetoothIcon,
  cellular: cellularIcon,
  ethernet: ethernetIcon,
  infrared: infraredIcon,
  infrarouge: infraredIcon,
  ir: infraredIcon,
  matter: matterIcon,
  poe: poeIcon,
  radio: radioIcon,
  '433mhz': radioIcon,
  '433 mhz': radioIcon,
  thread: threadIcon,
  'wi-fi': wifiIcon,
  wifi: wifiIcon,
  zigbee: zigbeeIcon,
  'z-wave': zWaveIcon,
  zwave: zWaveIcon,
};

const HOMEY_KEY = 'homey';

/** Aligné sur CompatibilityBadges — valeurs enum possibles pour le badge portrait Homey */
const normalizeConnectivityKey = val => {
  const n = (val || '').toLowerCase().trim();
  if (n === 'works with homey' || n === 'works-with-homey') {
    return HOMEY_KEY;
  }
  return n;
};

const isHomeyConnectivityValue = val => normalizeConnectivityKey(val) === HOMEY_KEY;

const SectionConnectivityGrid = ({ connectivity, config }) => {
  const values = Array.isArray(connectivity)
    ? connectivity
    : typeof connectivity === 'string' && connectivity.trim()
    ? [connectivity]
    : [];

  if (values.length === 0) {
    return <p className={css.smartAdvantageInfoValue}>Non renseigné</p>;
  }

  const fieldConfig = config?.listing?.listingFields?.find(f => f.key === 'connectivity');
  const getLabel = val => {
    if (fieldConfig?.enumOptions) {
      const option = fieldConfig.enumOptions.find(opt => opt.option === val);
      if (option) return option.label;
    }
    return val;
  };

  const homeyValues = values.filter(isHomeyConnectivityValue);
  const otherValues = values.filter(val => !isHomeyConnectivityValue(val));
  const hasHomey = homeyValues.length > 0;
  const homeyLabel =
    homeyValues[0] != null ? getLabel(homeyValues[0]) : 'Works with Homey';

  const renderItem = (val, index) => {
    const normalized = normalizeConnectivityKey(val);
    const iconSrc = CONNECTIVITY_ICON_MAP[normalized];
    const label = getLabel(val);

    return (
      <div key={`${val}-${index}`} className={css.connectivityItem}>
        <div className={css.connectivityIconSlot}>
          {iconSrc ? (
            <img src={iconSrc} alt={label} className={css.connectivityIcon} />
          ) : (
            <div className={css.connectivityIconPlaceholder} aria-hidden="true" />
          )}
        </div>
        <span className={css.connectivityLabel}>{label}</span>
      </div>
    );
  };

  const homeyColumn = (
    <div className={css.connectivityHomeyColumn}>
      <img
        src={worksWithHomeyBadge}
        alt={homeyLabel}
        className={classNames(css.connectivityHomeyBadge, css.homeyBadgeImage)}
      />
      <span className={css.connectivityLabel}>{homeyLabel}</span>
    </div>
  );

  if (hasHomey && otherValues.length > 0) {
    return (
      <div className={classNames(css.connectivityGrid, css.connectivityGridWithHomey)}>
        <div className={css.connectivityGridMain}>{otherValues.map(renderItem)}</div>
        {homeyColumn}
      </div>
    );
  }

  if (hasHomey && otherValues.length === 0) {
    return (
      <div className={classNames(css.connectivityGrid, css.connectivityGridHomeyOnly)}>{homeyColumn}</div>
    );
  }

  return <div className={css.connectivityGrid}>{values.map(renderItem)}</div>;
};

export default SectionConnectivityGrid;
